import { createTheme, alpha, Theme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeText {
    muted: string;
    hint: string;
    highlight: string;
  }

  interface RoleColors {
    superadmin: string;
    customeradmin: string;
    clientadmin: string;
    user: string;
    sdkuser: string;
    [key: string]: string; // optional fallback for dynamic roles
  }

  interface MedallionColors {
    gold: string;
    silver: string;
    bronze: string;
  }

  interface BrandColors {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    primaryGlow: string;
  }

  interface StatusColors {
    completed: string;
    success: string;
    running: string;
    pending: string;
    warning: string;
    failed: string;
    cancelled: string;
    inactive: string;
    notStarted: string;
    partial: string;
    bronze: string;
    silver: string;
    gold: string;
  }

  // add strongly-typed codeBlock config to theme
  interface Theme {
    roleColors: RoleColors;
    medallionColors: MedallionColors;
    brandColors: BrandColors;
    statusColors: StatusColors;
    chartPalette: string[];

    codeBlock: {
      background: string;
      color: string;
      fontFamily: string;
      padding: string;
      borderRadius: number;
      whiteSpace: string;
      lineHeight: number;
      fontSize: number;
      fontWeight?: string | number;
    };

    promptPreview: {
      background: string;
      borderColor: string;
      color: string;
      fontFamily: string;
      fontSize: string;
    };
  }
  interface ThemeOptions {
    roleColors?: Partial<RoleColors>;
    medallionColors?: Partial<MedallionColors>;
    brandColors?: Partial<BrandColors>;
    statusColors?: Partial<StatusColors>;
    chartPalette?: string[];
    codeBlock?: Partial<Theme['codeBlock']>;
    promptPreview?: Partial<Theme['promptPreview']>;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: '1px solid #E5E7EB',
          // borderRadius: '4px',
          // backgroundColor: theme.palette.grey[100],
          minHeight: '40px',
          // '& .MuiTabs-indicator': {
          //   display: 'none', // remove underline indicator
          // },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: '40px',
          padding: '6px 16px',

          '&:hover': {
            backgroundColor: '#F3F4F6',
            color: theme.palette.primary.main,
          },

          '&.Mui-selected': {
            fontWeight: 600,
          },
        }),
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        select: {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 6,
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          paddingTop: '8px',
          paddingBottom: '8px',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        *::-webkit-scrollbar {
          width: 0.4em;
          height: 0.6em;
          background: transparent;
        }

        *::-webkit-scrollbar-thumb {
          background-color: transparent;
          border-radius: 8px;
          transition: background-color 0.3s ease;
        }

        *:hover::-webkit-scrollbar-thumb {
          background-color:rgba(105, 94, 168, 0.23);
        }

        *::-webkit-scrollbar-thumb:hover {
          background-color: rgba(105, 94, 168, 0.4);
        }

        *::-webkit-scrollbar-track {
          background: transparent;
        }
      `,
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#9B93D4',
          fontSize: '12px',
        },
        arrow: {
          color: '#9B93D4',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#695ea8',
      dark: '#554b8e',
      light: '#9B93D4',
      // highlight: '#9B93D4',
      // highlight: '#9B93D4',
    },
    error: {
      main: '#bf0b0b',
      // dark: '#d32f2f',
      // light: '#f6a9a9',
    },
    text: {
      primary: '#475569',
      secondary: '#313131',
      disabled: '#94A3B8',
      hint: '#94A3B8', //Need to add meaningful color
      muted: '#CBD5E1', //Need to add meaningful color
      highlight: '#374151', // Add your slightly darker grey here
    },
  },

  codeBlock: {
    background: '#f5f5f5', // light grey
    color: '#3d3a3a', // very dark grey
    fontFamily: 'monospace',
    padding: '18px',
    borderRadius: 8,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
    fontSize: 13,
    fontWeight: 'bold',
  },
  promptPreview: {
    background: '#1E1E2E',
    borderColor: alpha('#695ea8', 0.3),
    color: '#CDD6F4',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  roleColors: {
    superadmin: '#c62828', // red for admins
    customeradmin: '#7b1fa2', // purple for managers
    clientadmin: '#2e7d32', // green for normal users
    user: '#ef6c00', // neutral grey for guests
    sdkuser: '#695ea8', // purple for SDK users
  },
  brandColors: {
    primary: '#695ea8',
    primaryDark: '#554b8e',
    primaryLight: '#9B93D4',
    primaryGlow: 'rgba(105, 94, 168, 0.3)',
  },
  statusColors: {
    completed: '#4caf50',
    success: '#16A34A',
    running: '#3b82f6',
    pending: '#D97706',
    warning: '#f59e0b',
    failed: '#DC2626',
    cancelled: '#94A3B8',
    inactive: '#94A3B8',
    notStarted: '#94A3B8',
    partial: '#D97706',
    bronze: '#F97316',
    silver: '#64748B',
    gold: '#EAB308',
  },
  medallionColors: {
    gold: '#EAB308',
    silver: '#9CA3AF',
    bronze: '#F97316',
  },
  chartPalette: [
    '#695ea8',
    '#2563EB',
    '#059669',
    '#D97706',
    '#DC2626',
    '#0891B2',
    '#7C3AED',
    '#65A30D',
    '#DB2777',
    '#0F766E',
    '#EA580C',
    '#4F46E5',
    '#16A34A',
    '#0284C7',
    '#BE123C',
    '#9333EA',
    '#CA8A04',
    '#047857',
    '#1D4ED8',
    '#C2410C',
    '#64748B',
    '#0E7490',
    '#A21CAF',
    '#15803D',
    '#B45309',
    '#4338CA',
    '#E11D48',
    '#0369A1',
    '#4D7C0F',
    '#7F1D1D',
  ],
} as ThemeOptions);

export default theme;
